/**
 * Public Relations › Press Con — the wire shapes.
 *
 * Every nullable field is declared `| null` and arrives as `undefined` (the server omits nulls), so
 * compare off the wire with `== null` / `!= null` only.
 */

/** `PressConferenceStatus` — numeric on the wire, mirrored member for member. */
export const PRESS_CONFERENCE_STATUS = {
  scheduled: 0,
  held: 1,
  cancelled: 2,
} as const;

export type PressConferenceStatus = (typeof PRESS_CONFERENCE_STATUS)[keyof typeof PRESS_CONFERENCE_STATUS];

/** The `conferenceStatus` FACET's values — the enum NAMES, which the server parses (facets stay strings). */
export const CONFERENCE_STATUS_FACET = {
  scheduled: 'Scheduled',
  held: 'Held',
  cancelled: 'Cancelled',
} as const;

/** One press conference, as the list and the profile both carry it. */
export interface PressConferenceItem {
  id: string;
  referenceNumber: string;
  purpose: string;
  /** A CALENDAR date, `YYYY-MM-DD` — `formatDateOnly`, never the instant path. */
  scheduledDate: string;
  /** A time of day, `HH:mm:ss` — `formatClockTime`, never the instant path. */
  startTime: string;
  endTime?: string | null;
  venue: string;
  remarks?: string | null;
  status: PressConferenceStatus;
  /** The server's label for `status`. */
  statusLabel: string;
  /** When it was marked held or cancelled — an instant. */
  statusChangedAt?: string | null;
  cancellationReason?: string | null;
  requestorType?: PressConferenceRequestorType | null;
  requestorTypeLabel?: string | null;
  /** An ORGANIZATION's name — not a person, so it travels. */
  requestorOrganization?: string | null;
  /** The external requestor's named person — an id for the one batch resolve, NEVER a name. */
  requestorPersonId?: string | null;
  requestorPersonnelId?: string | null;
  /** The internal requestor's PersonId, for the same batch resolve. */
  requestorPersonnelPersonId?: string | null;
  requestorEmployeeNumber?: string | null;
  speakerCount: number;
  /** The live speakers — on the profile, absent on the list. */
  speakers?: PressConferenceSpeakerItem[] | null;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
  isActive: boolean;
}

export interface PressConferenceRequest {
  purpose: string;
  scheduledDate: string | null;
  /** `HH:mm:ss` — what `TimeOnly` binds from. */
  startTime: string | null;
  endTime: string | null;
  venue: string;
  remarks: string | null;
  requestorType: PressConferenceRequestorType | null;
  requestorOrganization: string | null;
  /**
   * The name parts travel on the REQUEST only. All blank on an edit of an external requestor
   * already named means KEEP the person recorded; filled, they replace it.
   */
  requestorSalutation: string | null;
  requestorFirstName: string | null;
  requestorMiddleName: string | null;
  requestorLastName: string | null;
  requestorSuffix: string | null;
  requestorPersonnelId: string | null;
  /** The WHOLE set — the save replaces it. */
  speakerPersonnelIds: string[];
}

/** `PressConferenceRequestorType` — numeric on the wire, mirrored member for member. */
export const PRESS_CONFERENCE_REQUESTOR_TYPE = {
  external: 0,
  internal: 1,
} as const;

export type PressConferenceRequestorType =
  (typeof PRESS_CONFERENCE_REQUESTOR_TYPE)[keyof typeof PRESS_CONFERENCE_REQUESTOR_TYPE];

/** One speaker — ids and the employee number, never a name. */
export interface PressConferenceSpeakerItem {
  personnelId: string;
  personId?: string | null;
  employeeNumber?: string | null;
}

export interface PressConferenceCalendarFilters {
  search?: string;
  status?: string;
  conferenceStatus?: string;
}

export interface PressConferenceCalendarEntry {
  pressConferenceId: string;
  referenceNumber: string;
  purpose: string;
  date: string;
  startTime: string;
  endTime?: string | null;
  venue: string;
  status: PressConferenceStatus;
  statusLabel: string;
}

/** Where the module lives. Every route nests under it. */
export const PRESS_CONFERENCES_PATH = '/public-relations/press-conferences';

export const pressConferencePath = (id: string) => `${PRESS_CONFERENCES_PATH}/${id}`;

/** The module's audit entity types — what its History Logs and audit popover filter on. */
export const PRESS_CONFERENCE_AUDIT_TYPES = ['PressConference'];

/** `PressConferenceFieldRules` — the server's caps, mirrored for `maxLength`. */
export const PRESS_CONFERENCE_PURPOSE_MAX = 300;
export const PRESS_CONFERENCE_VENUE_MAX = 300;
export const PRESS_CONFERENCE_REMARKS_MAX = 2000;
export const PRESS_CONFERENCE_CANCELLATION_REASON_MAX = 500;

/**
 * The calendar dot per state — the tiles above the list carry the same dots, so they ARE the
 * year view's legend. **No amber**: the year view sits on an INDEX page, where amber is chrome.
 */
export const CONFERENCE_STATUS_DOT: Record<PressConferenceStatus, string> = {
  [PRESS_CONFERENCE_STATUS.scheduled]: 'bg-blue-500',
  [PRESS_CONFERENCE_STATUS.held]: 'bg-emerald-500',
  [PRESS_CONFERENCE_STATUS.cancelled]: 'bg-slate-400',
};

/** Why a conference cannot be changed right now, or null — the server's lock, stated before the click. */
export function pressConferenceLock(conference: Pick<PressConferenceItem, 'isActive' | 'status' | 'statusLabel'>): string | null {
  if (!conference.isActive) return 'This press conference is deleted. Restore it first.';
  if (conference.status !== PRESS_CONFERENCE_STATUS.scheduled) {
    return `This press conference is ${conference.statusLabel.toLowerCase()}, so it cannot be changed unless it is reopened.`;
  }
  return null;
}

/** Why it cannot be marked held, or null — the lock first, then its own day (today, `YYYY-MM-DD`). */
export function markHeldBlock(
  conference: Pick<PressConferenceItem, 'isActive' | 'status' | 'statusLabel' | 'scheduledDate'>,
  today: string,
): string | null {
  return (
    pressConferenceLock(conference) ??
    (conference.scheduledDate > today ? 'It can be marked held on or after the day it is scheduled for.' : null)
  );
}

/** Why it cannot be reopened, or null — it must be live and already held or cancelled. */
export function reopenBlock(conference: Pick<PressConferenceItem, 'isActive' | 'status'>): string | null {
  if (!conference.isActive) return 'This press conference is deleted. Restore it first.';
  if (conference.status === PRESS_CONFERENCE_STATUS.scheduled) return 'This press conference is already scheduled.';
  return null;
}
